import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
} from '@mui/material';
import { CognitoService } from '../../services/api/auth/cognito.service';
import Notifications from '../common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import { useDispatch } from 'react-redux';
import ContactDeveloper from './ContactDeveloper';
import { ROUTES } from '../../config/constants';
import { AppDispatch } from '../../store';
import { userEndpoints } from '../../services/api/endpoints/user.endpoint';
import { RequestedUser } from '../../types/user';
import { loginUser } from '../../store/slices/authSlice';
import { createRequstedUser, requestedUserLongPolling } from './RequestedUserCreate';
import { setSelectedOrganizationId } from '../../store/slices/organizationContextSlice';
import { organizationEndpoints } from '../../services/api/endpoints/organisation.endpoint';
import { setSelectedOrganisation } from '../../store/slices/organizationSlice';
import { Organisation } from '../../types/organization';
import BrokenLink from './BrokenLink';

enum ActivationStep {
  FETCH_USER = 0,
  ENTER_PASSWORD = 1,
  CREATE_COGNITO_USER = 2,
  CONFIRM_SIGNUP = 3,
  CREATE_USER = 4,
  COMPLETE = 5,
}

export default function UserActivateView() {
  const { id, uuid } = useParams();
  const navigate = useNavigate();

  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const [data, setData] = useState<RequestedUser[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<ActivationStep>(ActivationStep.FETCH_USER);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [canResendCode, setCanResendCode] = useState(false);
  const [resendTimeout, setResendTimeout] = useState(60);
  const [cognitoId, setCognitoId] = useState('');
  const [showContactDeveloper, setShowContactDeveloper] = useState(false);
  const [showBrokenLink, setShowBrokenLink] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const stepLabels = [
    'Fetching User details',
    'Enter your password',
    'Creating Cognito User',
    'Confirming signup',
    'Creating user',
    'Activation complete',
  ];

  //   const passwordsValid = password.length >= 8 && password === confirmPassword;
  // Password validation function
  const isValidPassword = (password: string) => {
    const hasNumber = /\d/.test(password); // At least one number
    const hasUpperCase = /[A-Z]/.test(password); // At least one uppercase letter
    const hasSpecialChar = /[!@#$%^&*()_\-+=<>?{}[\]~]/.test(password);
    //const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    return password.length >= 8 && hasNumber && hasUpperCase && hasSpecialChar;
  };

  // Combined validation check
  const passwordsValid = isValidPassword(password) && password === confirmPassword;

  // Function to fetch organization data
  const fetchUserDetails = async () => {
    try {
      // Check if organization ID is missing
      if (!id || !uuid) {
        setShowBrokenLink(true);
        throw new Error('Organization ID or Requested User UUID is missing');
        return;
      }

      // Fetch organization data from API
      const response: RequestedUser[] = await userEndpoints.getRequstedUser(id, uuid);
      // If response is empty, show broken link
      if (!response || response.length === 0) {
        setShowBrokenLink(true);
        return;
      }
      if (response[0]?.status === 'active') {
        setCurrentStep(ActivationStep.COMPLETE);
        dispatch(setSelectedOrganizationId(id!));
        const org: Organisation = await organizationEndpoints.getSingleOrganisation(id!);
        dispatch(setSelectedOrganisation(org));
      }
      if (response[0]?.status === 'failed_creation') {
        setShowContactDeveloper(true);
      }

      // Check if response is empty
      if (!response || response.length === 0) throw new Error('Failed to fetch User data');

      // Set data and current step
      setData(response);
      setCurrentStep(ActivationStep.ENTER_PASSWORD);
    } catch (err: any) {
      setShowBrokenLink(true);
      setNotification({
        open: true,
        message: err.message || 'Error fetching organization details',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCognitoUser = async () => {
    try {
      setLoading(true);
      const adminEmail = data?.[0]?.email;
      if (!adminEmail) throw new Error('No email found for the admin');

      const userSignup = await CognitoService.signUp({
        email: adminEmail,
        password: password,
        orgId: id!,
        role: data?.[0]?.role || 'client_user',
      });

      setCognitoId(userSignup.userId!);

      // setCanResendCode(true);
      setCurrentStep(ActivationStep.CONFIRM_SIGNUP);
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.message || 'Error creating admin user',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCode = async () => {
    try {
      setLoading(true);
      const adminEmail = data?.[0]?.email;
      if (!adminEmail) throw new Error('No email found for confirmation');

      await CognitoService.confirmCode(adminEmail, confirmationCode);
      // const userSignIn = await CognitoService.signIn(adminEmail, password);
      await dispatch(loginUser({ email: adminEmail, password }));

      // setCognitoId(userSignIn.user?.id!);

      setCurrentStep(ActivationStep.CREATE_USER);
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.message || 'Error confirming signup',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      const adminEmail = data?.[0]?.email;
      if (!adminEmail) throw new Error('No email found for resending code');

      await CognitoService.resendSignUpCode(adminEmail);

      setNotification({
        open: true,
        message: 'New confirmation code sent successfully',
        severity: SEVERITY.SUCCESS,
      });

      // Reset countdown
      setCanResendCode(false);
      setResendTimeout(60);

      let countdown = 60;
      const timer = setInterval(() => {
        countdown -= 1;
        setResendTimeout(countdown);
        if (countdown <= 0) {
          clearInterval(timer);
          setCanResendCode(true);
        }
      }, 1000);
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.message || 'Error resending confirmation code',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setLoading(true);
      const clientEmail = data?.[0]?.email;
      const clientFirstName = data?.[0]?.firstName;
      const clientRole = data?.[0]?.role;

      if (!clientEmail || !clientFirstName || !id || !uuid || !clientRole) {
        throw new Error('Organization data is missing');
      }
      await createRequstedUser(id!, {
        email: clientEmail,
        cognitoId: cognitoId,
        firstName: clientFirstName,
        ruuid: uuid,
        role: clientRole,
        status: 'active',
        hasSeenTour: false,
      });
      const isActive = await requestedUserLongPolling(id!, cognitoId);

      if (!isActive) {
        <ContactDeveloper message="Failed to create User" />;
      }

      dispatch(setSelectedOrganizationId(id!));
      const org: Organisation = await organizationEndpoints.getSingleOrganisation(id!);
      if (org) {
        dispatch(setSelectedOrganisation(org));
      } else {
        console.error('Organisation data is undefined or empty.');
      }

      setCurrentStep(ActivationStep.COMPLETE);
    } catch (err: any) {
      setShowContactDeveloper(true);
      setNotification({
        open: true,
        message: err.message || 'Error creating organization',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep === ActivationStep.ENTER_PASSWORD && passwordsValid) {
      setCurrentStep(ActivationStep.CREATE_COGNITO_USER);
    }
  };

  useEffect(() => {
    if (currentStep === ActivationStep.FETCH_USER) {
      fetchUserDetails();
    } else if (currentStep === ActivationStep.CREATE_COGNITO_USER) {
      handleCreateCognitoUser();
    } else if (currentStep === ActivationStep.CREATE_USER) {
      handleCreateUser();
    } else if (currentStep === ActivationStep.CONFIRM_SIGNUP) {
      setCanResendCode(false);
      setResendTimeout(60);

      let countdown = 60;
      const timer = setInterval(() => {
        countdown -= 1;
        setResendTimeout(countdown);
        if (countdown <= 0) {
          clearInterval(timer);
          setCanResendCode(true);
        }
      }, 1000);

      return () => clearInterval(timer);
    } else if (currentStep === ActivationStep.COMPLETE) {
      navigate(ROUTES.DASHBOARD);
    }
  }, [currentStep]);

  if (showBrokenLink) {
    return <BrokenLink />;
  }

  if (showContactDeveloper) {
    return <ContactDeveloper message="Failed to create User" />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: 4,
      }}
    >
      <Typography variant="h4" gutterBottom>
        Activating User
      </Typography>

      <Stepper activeStep={currentStep} alternativeLabel sx={{ width: '100%', maxWidth: 800 }}>
        {stepLabels.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body1">{stepLabels[currentStep]}...</Typography>
        </Box>
      ) : (
        <>
          {currentStep === ActivationStep.ENTER_PASSWORD && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '300px' }}>
              <TextField
                label="Email"
                type="email"
                defaultValue={data?.[0]?.email}
                disabled
                fullWidth
              />
              {/* <TextField
                label="Enter Your Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                fullWidth
                error={password.length > 0 && password.length < 8}
                helperText={
                  password.length > 0 && password.length < 8
                    ? 'Password must be at least 8 characters'
                    : ''
                }
              />
              <TextField
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                fullWidth
                error={confirmPassword.length > 0 && password !== confirmPassword}
                helperText={
                  confirmPassword.length > 0 && password !== confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
              /> */}
              <TextField
                label="Enter Your Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                fullWidth
                error={password.length > 0 && !isValidPassword(password)}
                helperText={
                  password.length > 0 && !isValidPassword(password)
                    ? 'Password must be at least 8 characters, include a number, a special character and an uppercase letter.'
                    : ''
                }
              />
              <TextField
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                fullWidth
                error={confirmPassword.length > 0 && password !== confirmPassword}
                helperText={
                  confirmPassword.length > 0 && password !== confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
              />
              <Button onClick={handleNext} variant="contained" disabled={!passwordsValid}>
                Next
              </Button>
            </Box>
          )}

          {currentStep === ActivationStep.CONFIRM_SIGNUP && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '300px' }}>
              <TextField
                type="number"
                label="Confirmation Code"
                value={confirmationCode}
                onChange={e => setConfirmationCode(e.target.value)}
                fullWidth
              />
              <Button onClick={handleConfirmCode} variant="contained" disabled={!confirmationCode}>
                Confirm
              </Button>
              <Button
                onClick={handleResendCode}
                variant="outlined"
                disabled={!canResendCode || loading}
                sx={{ mt: 2 }}
              >
                {canResendCode ? 'Resend Code' : `Resend available in ${resendTimeout}s`}
              </Button>
            </Box>
          )}
        </>
      )}

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
}
